/**
 * admin/makua/AdminActivities.js — Operations → Activities & Add-ons (V2.1, Page 7).
 *
 * The one library of everything that can be added to a stay. Each dated
 * retreat (Retreat Schedule → a date → Add-ons) picks from here, with its
 * own day, time, price, places and whether it's included, paid or on
 * request. Changing an add-on here never changes what someone already
 * booked: their price was fixed when they booked it.
 */
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { usePermissions, hasPermission } from "../permissions";
import { bookingsAPI, mediaUrl } from "../../services/api";
import { useLoad, Field, Modal, money, Badge } from "./ops";
import { useStored, useSort, SortTh, useReorder, ReorderHandle, useUnsaved, changed, Gallery } from "./kit";
import "./ops.css";

export default function AdminActivities() {
  return (
    <AdminLayout pageTitle="Activities & Add-ons" pageSubtitle="The library of experiences, services and ceremonies each retreat date can offer."
      permission="blocks.view" feature="reservations">
      <Library />
    </AdminLayout>
  );
}

const STATUS = (a) => (a.archived_at ? { key: "archived", label: "Archived", tone: "red" } : a.active ? { key: "active", label: "Active", tone: "green" } : { key: "off", label: "Off", tone: "amber" });
const INCLUSION = { paid: "Paid", included: "Included", request: "On request" };

function Library() {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "blocks.edit");
  const { data, error, reload } = useLoad(() => bookingsAPI.catalog.activities(), []);
  const [q, setQ] = useStored("act:q", "");
  const [cat, setCat] = useStored("act:cat", "");
  const [status, setStatus] = useStored("act:status", "current");
  const [edit, setEdit] = useState(null);
  const all = useMemo(() => data || [], [data]);
  const categories = [...new Set(all.map((a) => a.category).filter(Boolean))].sort();

  const needle = q.trim().toLowerCase();
  const filtered = all.filter((a) => {
    const st = STATUS(a).key;
    if (status === "current" && st === "archived") return false;
    if (status && status !== "current" && st !== status) return false;
    if (cat && a.category !== cat) return false;
    return !needle || [a.name, a.summary, a.category, a.staff_requirement].some((v) => String(v || "").toLowerCase().includes(needle));
  });
  const accessors = useMemo(() => ({
    name: (a) => a.name, category: (a) => a.category, duration: (a) => a.duration_label, price: (a) => Number(a.unit_price),
    status: (a) => STATUS(a).label, used: (a) => Number(a.used_in), booked: (a) => Number(a.booked),
  }), []);
  const { rows, sort, setSort } = useSort(filtered, accessors, null, "act:sort");
  // Custom order: the saved one (sort_order), shown when no column sort is on; dragging changes it.
  const custom = !sort.key;
  const canDrag = canEdit && custom && !needle && !cat && status === "current";
  const order = useReorder(rows, async (ids) => {
    // The whole library's order: the dragged view, then anything filtered out, in its old place.
    const rest = all.map((a) => a.id).filter((id) => !ids.includes(id));
    await bookingsAPI.catalog.reorderActivities([...ids, ...rest]);
    toast.success("Order saved.");
  });
  const shown = canDrag ? order.items : rows;

  const duplicate = async (a) => {
    try { const r = await bookingsAPI.catalog.duplicateActivity(a.id); toast.success(r.message); await reload(); } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="ops">
      <div className="ops-bar ops-toolbar">
        <Field label="Search" className="ops-search"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, category, staff" /></Field>
        <Field label="Category">
          <select value={cat} onChange={(e) => setCat(e.target.value)}><option value="">All</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="current">Active and off</option><option value="active">Active</option><option value="off">Off</option><option value="archived">Archived</option><option value="">Everything</option>
          </select>
        </Field>
        <span className="ops-count">{shown.length} of {all.length}</span>
        <span className="ops-spacer" />
        {!custom && <button className="ops-btn ops-btn--sm" onClick={() => setSort({ key: null, dir: "asc" })}>Back to custom order</button>}
        {canEdit && <button className="ops-btn ops-btn--primary" onClick={() => setEdit({})}>New add-on</button>}
      </div>
      <p className="ops-muted" style={{ margin: 0 }}>
        {canDrag ? "Drag rows (or use the arrows) to set the order guests see add-ons in. Column sorting doesn't change that order." : custom ? "Clear the search and filters to change the order." : "Sorted by a column; the saved custom order is unchanged."}
      </p>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-table-wrap">
        <table className="ops-table">
          <thead>
            <tr>
              {canDrag && <th><span className="ops-visually-hidden">Order</span></th>}
              <SortTh k="name" sort={sort} setSort={setSort}>Add-on</SortTh>
              <SortTh k="category" sort={sort} setSort={setSort}>Category</SortTh>
              <SortTh k="duration" sort={sort} setSort={setSort}>Length</SortTh>
              <SortTh k="price" sort={sort} setSort={setSort} className="num">Price</SortTh>
              <th className="num">With a retreat</th>
              <th>Offered as</th>
              <SortTh k="status" sort={sort} setSort={setSort}>Status</SortTh>
              <SortTh k="used" sort={sort} setSort={setSort} className="num">Retreat dates</SortTh>
              <SortTh k="booked" sort={sort} setSort={setSort} className="num">Booked</SortTh>
              <th />
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={11} className="ops-empty">Loading…</td></tr>}
            {data && !shown.length && <tr><td colSpan={11} className="ops-empty">Nothing matches.{canEdit ? " Use New add-on to add one." : ""}</td></tr>}
            {shown.map((a) => {
              const st = STATUS(a);
              return (
                <tr key={a.id} {...(canDrag ? order.rowProps(a.id) : {})}>
                  {canDrag && <td><ReorderHandle r={order} id={a.id} label={a.name} /></td>}
                  <td>
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                      {a.image_path ? <img src={mediaUrl(a.image_path)} alt="" width="48" height="34" style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0 }} /> : null}
                      <div><button className="ops-row-btn" onClick={() => setEdit(a)}>{a.name}</button>
                        <span className="ops-sub">{a.summary || ""}{a.photo_count ? ` · ${a.photo_count} photo${a.photo_count === 1 ? "" : "s"}` : ""}</span></div>
                    </div>
                  </td>
                  <td>{a.category}</td>
                  <td>{a.duration_label || "—"}</td>
                  <td className="num">{money(a.unit_price)}</td>
                  <td className="num">{a.retreat_price != null ? money(a.retreat_price) : "—"}</td>
                  <td>{INCLUSION[a.default_inclusion] || "Paid"}{a.requires_clearance ? <span className="ops-sub">needs clearance</span> : null}</td>
                  <td><Badge tone={st.tone}>{st.label}</Badge></td>
                  <td className="num">{a.used_in}</td>
                  <td className="num">{a.booked}</td>
                  <td className="num">
                    <button className="ops-btn ops-btn--sm" onClick={() => setEdit(a)}>{canEdit ? "Edit" : "View"}</button>
                    {canEdit && <>{" "}<button className="ops-btn ops-btn--sm" onClick={() => duplicate(a)}>Duplicate</button></>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {edit && <AddonModal a={edit} canEdit={canEdit} categories={categories} onClose={() => setEdit(null)} onSaved={(close) => { reload(); if (close) setEdit(null); }} />}
    </div>
  );
}

const form = (a) => ({
  name: a.name || "", summary: a.summary || "", description: a.description || "", category: a.category || "Activities",
  duration_label: a.duration_label || "", unit_price: Number(a.unit_price || 0), retreat_price: a.retreat_price ?? "",
  default_capacity: a.default_capacity ?? 10, requires_clearance: !!a.requires_clearance, staff_requirement: a.staff_requirement || "",
  default_inclusion: a.default_inclusion || "paid", sort_order: a.sort_order || 0, active: a.id ? !!a.active : true,
});

function AddonModal({ a, canEdit, categories, onClose, onSaved }) {
  const [id, setId] = useState(a.id || null);
  const [base, setBase] = useState(() => form(a));
  const [f, setF] = useState(base);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const r = await bookingsAPI.catalog.saveActivity({ ...f, retreat_price: f.retreat_price === "" ? null : f.retreat_price }, id);
      setBase(f);
      if (!id) { setId(r.data.id); toast.success("Add-on added. Now add its photos."); onSaved(false); return; }
      toast.success(r.message || "Saved."); onSaved(false);
    } catch (err) { toast.error(err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${base.name}? An add-on that's offered on a retreat date or was booked is archived instead; bookings keep it.`)) return;
    try { const r = await bookingsAPI.catalog.removeActivity(id); toast.success(r.message); onSaved(true); } catch (err) { toast.error(err.message); }
  };
  const archived = !!a.archived_at;
  return (
    <Modal title={id ? (canEdit ? `Edit ${base.name}` : base.name) : "New add-on"} onClose={guard(onClose)} wide>
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="ops-grid ops-grid--3">
          <Field label="Name" className="ops-span-2"><input value={f.name} onChange={set("name")} /></Field>
          <Field label="Category" hint="Groups it on the booking page"><input value={f.category} onChange={set("category")} list="addon-cats" />
            <datalist id="addon-cats">{[...new Set(["Activities", "Plant medicine", "Wellness and integration", ...categories])].map((c) => <option key={c} value={c} />)}</datalist></Field>
          <Field label="Short description" className="ops-span-2"><input value={f.summary} onChange={set("summary")} /></Field>
          <Field label="Length"><input value={f.duration_label} onChange={set("duration_label")} placeholder="Half day, 90 minutes" /></Field>
          <Field label="Price (USD)"><input type="number" min="0" value={f.unit_price} onChange={set("unit_price")} /></Field>
          <Field label="Price when booked with a retreat" hint="Optional. Shown as a saving."><input type="number" min="0" value={f.retreat_price} onChange={set("retreat_price")} /></Field>
          <Field label="Usual places per session" hint="Each date can set its own"><input type="number" min="0" value={f.default_capacity} onChange={set("default_capacity")} /></Field>
          <Field label="Usually offered as" hint="The starting choice when a date adds it">
            <select value={f.default_inclusion} onChange={set("default_inclusion")}><option value="paid">Paid add-on</option><option value="included">Included in the retreat</option><option value="request">On request</option></select>
          </Field>
          <Field label="Staff or provider needed" className="ops-span-2" hint="e.g. 1 facilitator, 2 horses and a guide"><input value={f.staff_requirement} onChange={set("staff_requirement")} /></Field>
          <Field label="Full description" className="ops-span-all"><textarea value={f.description} onChange={set("description")} rows={4} /></Field>
        </div>
        <label className="ops-check"><input type="checkbox" checked={f.requires_clearance} onChange={set("requires_clearance")} />Needs medical clearance: requested at booking, confirmed and charged only once the guest is cleared</label>
        <label className="ops-check"><input type="checkbox" checked={f.active} onChange={set("active")} />Active: can be offered on retreat dates{archived ? " (restores it from the archive)" : ""}</label>
        {id && <p className="ops-note">Changing the price or rules here applies to new bookings. Add-ons already booked keep the price they were booked at.</p>}
      </fieldset>
      {canEdit && (
        <div className="ops-actions">
          {id && <button className="ops-btn ops-btn--danger" onClick={remove} style={{ marginRight: "auto" }}>Delete</button>}
          {dirty && <span className="ops-dirty">Unsaved changes</span>}
          <button className="ops-btn" onClick={guard(onClose)}>Close</button>
          <button className="ops-btn ops-btn--primary" disabled={!f.name.trim() || (id && !dirty)} onClick={save}>{id ? "Save changes" : "Add add-on"}</button>
        </div>
      )}
      {id ? <Gallery kind="activity" ownerId={id} title={`Photos of ${base.name || "this add-on"}`} hint="The primary photo is the one guests see on the booking page." canEdit={canEdit} onChange={() => onSaved(false)} />
        : <p className="ops-note">Add the add-on first; then its photos.</p>}
    </Modal>
  );
}
